/**
 * React Query hooks for notification-related operations
 * Following the 2-Strategy State Management pattern - server state handled by React Query
 */
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { ApiClientService, queryKeys } from '@splitfiesta/core';
import type {
  NotificationMarkReadDto,
  NotificationResponseDto,
  NotificationSettingsResponseDto,
  SuccessResponseDto,
  UpdateNotificationSettingsDto,
} from '@splitfiesta/shared';

import { useApi } from '@/hooks/useApi';

/**
 * Extended paginated response with unread and unseen counts
 */
interface PaginatedNotificationResponse {
  items: NotificationResponseDto[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  cursor?: string;
  unreadCount: number;
  unseenCount: number; // For Facebook-style badge count
}

/**
 * Hook to fetch paginated notifications
 */
export function useNotificationsQuery(
  params: { page?: number; limit?: number; unreadOnly?: boolean } = {},
  enabled = true
) {
  const api = useApi();
  const { page = 1, limit = 20, unreadOnly = false } = params;

  return useQuery({
    queryKey: queryKeys.notifications({ page, limit, unreadOnly }),
    queryFn: async () => {
      const response = await ApiClientService.notifications.list(api, {
        page,
        limit,
        unreadOnly,
      });
      return response as SuccessResponseDto<PaginatedNotificationResponse>;
    },
    enabled,
    staleTime: 0, // Always consider stale for immediate refresh
    gcTime: 5 * 60 * 1000, // 5 minutes cache
    refetchOnMount: 'always', // Always refetch on mount (PWA can't pull to refresh)
  });
}

/**
 * OPTIMIZED: Hook to fetch notifications with cursor-based infinite scrolling
 * Uses proper DynamoDB pagination for superior performance
 */
export function useNotificationsInfiniteQuery(
  params: {
    limit?: number;
    unreadOnly?: boolean;
    type?: string;
    search?: string;
  } = {}
) {
  const api = useApi();
  const { limit = 20, unreadOnly = false, type, search } = params;

  return useInfiniteQuery({
    queryKey: queryKeys.notificationsInfinite({
      limit,
      unreadOnly,
      type,
      search,
    }),
    queryFn: async ({ pageParam }) => {
      // ✅ OPTIMIZED: Use cursor-based pagination
      const response = await ApiClientService.notifications.listWithCursor(
        api,
        {
          cursor: pageParam, // pageParam is now a cursor string
          limit,
          unreadOnly,
          type,
          search,
        }
      );
      return response;
    },
    getNextPageParam: lastPage => {
      // Check both hasMore and cursor to prevent extra API calls
      const pageData = lastPage.data as {
        nextCursor?: string;
        hasMore?: boolean;
      };
      return pageData?.hasMore && pageData?.nextCursor
        ? pageData.nextCursor
        : undefined;
    },
    staleTime: 0, // Always consider stale for immediate refresh
    gcTime: 5 * 60 * 1000,
    initialPageParam: undefined as string | undefined, // Start with no cursor
    refetchOnMount: 'always', // Always refetch on mount (PWA can't pull to refresh)
  });
}

/**
 * LEGACY: Page-based infinite query for backward compatibility
 * Use useNotificationsInfiniteQuery() for new implementations
 */
export function useNotificationsInfiniteQueryLegacy(
  params: {
    limit?: number;
    unreadOnly?: boolean;
    type?: string;
    search?: string;
  } = {}
) {
  const api = useApi();
  const { limit = 20, unreadOnly = false, type, search } = params;

  return useInfiniteQuery({
    queryKey: [
      ...queryKeys.notificationsInfinite({
        limit,
        unreadOnly,
        type,
        search,
      }),
      'legacy',
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await ApiClientService.notifications.list(api, {
        page: pageParam,
        limit,
        unreadOnly,
        type,
        search,
      });
      return response as SuccessResponseDto<PaginatedNotificationResponse>;
    },
    getNextPageParam: lastPage => {
      const data = lastPage.data;
      const hasNextPage = data.page * data.limit < data.total;
      return hasNextPage ? data.page + 1 : undefined;
    },
    staleTime: 0, // Always consider stale for immediate refresh
    gcTime: 5 * 60 * 1000,
    initialPageParam: 1,
    refetchOnMount: 'always', // Always refetch on mount (PWA can't pull to refresh)
  });
}

/**
 * Hook to mark notifications as read
 */
export function useMarkNotificationsAsReadMutation() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      notificationIds?: string[];
      markAll?: boolean;
      /** Defaults to `true`. Set `false` to flip a selection back
          to unread (single-row toggle). `markAll` is read-only. */
      read?: boolean;
    }) => {
      const response = await ApiClientService.notifications.markAsRead(
        api,
        params.notificationIds,
        params.markAll,
        params.read
      );
      return response as SuccessResponseDto<NotificationMarkReadDto>;
    },
    onMutate: async params => {
      // Cancel any outgoing refetches to prevent overwriting our optimistic update
      await queryClient.cancelQueries({
        queryKey: queryKeys.notificationsInfinite(),
      });

      // Snapshot the previous value for potential rollback
      const previousData = queryClient.getQueryData<
        InfiniteData<SuccessResponseDto<PaginatedNotificationResponse>>
      >(queryKeys.notificationsInfinite());

      // Optimistically update ALL notification queries
      if (params.markAll) {
        // Mark all notifications as read
        queryClient.setQueriesData<
          InfiniteData<SuccessResponseDto<PaginatedNotificationResponse>>
        >(
          {
            predicate: query =>
              query.queryKey[0] === 'splitfiesta' &&
              query.queryKey[1] === 'notifications' &&
              query.queryKey[2] === 'infinite',
          },
          oldData => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              pages: oldData.pages.map((page, pageIndex) => ({
                ...page,
                data: {
                  ...page.data,
                  items: page.data.items.map(notification => ({
                    ...notification,
                    isRead: true,
                  })),
                  // Reset unread count to 0 on the first page
                  unreadCount: pageIndex === 0 ? 0 : page.data.unreadCount,
                },
              })),
            };
          }
        );

        // CRITICAL: Update the count query for bell counter
        queryClient.setQueryData(
          queryKeys.notificationsCount(),
          (oldData: unknown) => {
            if (!oldData || typeof oldData !== 'object') return oldData;
            const typedOldData = oldData as { data?: { unreadCount?: number } };
            return {
              ...typedOldData,
              data: {
                ...typedOldData.data,
                unreadCount: 0,
              },
            };
          }
        );

        // Also update regular notifications queries
        queryClient.setQueriesData<
          SuccessResponseDto<PaginatedNotificationResponse>
        >(
          {
            predicate: query =>
              query.queryKey[0] === 'splitfiesta' &&
              query.queryKey[1] === 'notifications' &&
              query.queryKey[2] !== 'infinite' &&
              query.queryKey[2] !== 'settings',
          },
          oldData => {
            if (!oldData) return oldData;

            return {
              ...oldData,
              data: {
                ...oldData.data,
                items: oldData.data.items.map(notification => ({
                  ...notification,
                  isRead: true,
                })),
                unreadCount: 0,
              },
            };
          }
        );
      } else if (params.notificationIds) {
        // Mark specific notifications. `read` is the *target* state —
        // defaults to true (mark-as-read, the historical behavior);
        // `false` flips a selection back to unread for the new
        // per-row "Mark as unread" affordance. Each delta below
        // counts items that would *transition* (e.g. flipping a
        // notification that's already in the target state is a
        // no-op and shouldn't move the unread counter).
        const targetRead = params.read ?? true;
        const notificationIdSet = new Set(params.notificationIds);

        let totalTransitions = 0;
        const firstInfiniteQuery = queryClient.getQueryData<
          InfiniteData<SuccessResponseDto<PaginatedNotificationResponse>>
        >(queryKeys.notificationsInfinite({}));

        if (firstInfiniteQuery) {
          for (const page of firstInfiniteQuery.pages) {
            for (const notification of page.data.items) {
              if (
                notificationIdSet.has(notification.id) &&
                notification.isRead !== targetRead
              ) {
                totalTransitions++;
              }
            }
          }
        }

        queryClient.setQueriesData<
          InfiniteData<SuccessResponseDto<PaginatedNotificationResponse>>
        >(
          {
            predicate: query =>
              query.queryKey[0] === 'splitfiesta' &&
              query.queryKey[1] === 'notifications' &&
              query.queryKey[2] === 'infinite',
          },
          oldData => {
            if (!oldData) return oldData;

            let localTransitions = 0;
            for (const page of oldData.pages) {
              for (const notification of page.data.items) {
                if (
                  notificationIdSet.has(notification.id) &&
                  notification.isRead !== targetRead
                ) {
                  localTransitions++;
                }
              }
            }

            return {
              ...oldData,
              pages: oldData.pages.map((page, pageIndex) => ({
                ...page,
                data: {
                  ...page.data,
                  items: page.data.items.map(notification =>
                    notificationIdSet.has(notification.id)
                      ? { ...notification, isRead: targetRead }
                      : notification
                  ),
                  // Marking-as-read decrements the counter, marking
                  // -as-unread increments it. Only the first page
                  // owns the badge total in the infinite query.
                  unreadCount:
                    pageIndex === 0
                      ? targetRead
                        ? Math.max(0, page.data.unreadCount - localTransitions)
                        : page.data.unreadCount + localTransitions
                      : page.data.unreadCount,
                },
              })),
            };
          }
        );

        // Update the bell-counter query (only if anything actually
        // transitioned — flipping an already-read row to read is
        // a no-op).
        if (totalTransitions > 0) {
          queryClient.setQueryData(
            queryKeys.notificationsCount(),
            (oldCountData: unknown) => {
              if (!oldCountData || typeof oldCountData !== 'object')
                return oldCountData;
              const typedOldCountData = oldCountData as {
                data?: { unreadCount?: number };
              };
              const prev = typedOldCountData.data?.unreadCount || 0;
              return {
                ...typedOldCountData,
                data: {
                  ...typedOldCountData.data,
                  unreadCount: targetRead
                    ? Math.max(0, prev - totalTransitions)
                    : prev + totalTransitions,
                },
              };
            }
          );
        }

        // Also update regular notifications queries
        queryClient.setQueriesData<
          SuccessResponseDto<PaginatedNotificationResponse>
        >(
          {
            predicate: query =>
              query.queryKey[0] === 'splitfiesta' &&
              query.queryKey[1] === 'notifications' &&
              query.queryKey[2] !== 'infinite' &&
              query.queryKey[2] !== 'settings',
          },
          oldData => {
            if (!oldData) return oldData;

            let localTransitions = 0;
            for (const notification of oldData.data.items) {
              if (
                notificationIdSet.has(notification.id) &&
                notification.isRead !== targetRead
              ) {
                localTransitions++;
              }
            }

            return {
              ...oldData,
              data: {
                ...oldData.data,
                items: oldData.data.items.map(notification =>
                  notificationIdSet.has(notification.id)
                    ? { ...notification, isRead: targetRead }
                    : notification
                ),
                unreadCount: targetRead
                  ? Math.max(0, oldData.data.unreadCount - localTransitions)
                  : oldData.data.unreadCount + localTransitions,
              },
            };
          }
        );
      }

      // Return context for potential rollback
      return { previousData };
    },
    onError: (_err, _params, context) => {
      // If the mutation fails, use the context to roll back
      if (context?.previousData) {
        queryClient.setQueryData(
          queryKeys.notificationsInfinite(),
          context.previousData
        );
      }
    },
    onSuccess: () => {
      // Don't invalidate immediately - let the optimistic update persist
      // Schedule a background refetch after a delay to ensure consistency
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.notificationsInfinite(),
          refetchType: 'active',
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.notifications(),
          refetchType: 'active',
        });
        // CRITICAL: Invalidate the count query to update the bell counter
        queryClient.invalidateQueries({
          queryKey: queryKeys.notificationsCount(),
          refetchType: 'active',
        });
      }, 1000); // Delay refetch by 1 second to let UI update smoothly
    },
  });
}

/**
 * Hook to mark a single notification as read
 */
export function useMarkNotificationAsReadMutation() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await ApiClientService.notifications.markAsRead(api, [
        notificationId,
      ]);
      return response as SuccessResponseDto<NotificationMarkReadDto>;
    },
    onMutate: async notificationId => {
      // Cancel any outgoing refetches to prevent overwriting our optimistic update
      await queryClient.cancelQueries({
        queryKey: queryKeys.notificationsInfinite(),
      });

      // Snapshot the previous value for potential rollback
      const previousData = queryClient.getQueryData<
        InfiniteData<SuccessResponseDto<PaginatedNotificationResponse>>
      >(queryKeys.notificationsInfinite());

      // Check if notification is unread BEFORE updating any caches
      let notificationWasUnread = false;
      const firstInfiniteQuery = queryClient.getQueryData<
        InfiniteData<SuccessResponseDto<PaginatedNotificationResponse>>
      >(queryKeys.notificationsInfinite({}));

      if (firstInfiniteQuery) {
        for (const page of firstInfiniteQuery.pages) {
          const notification = page.data.items.find(
            n => n.id === notificationId
          );
          if (notification && !notification.isRead) {
            notificationWasUnread = true;
            break;
          }
        }
      }

      // Optimistically update ALL notification queries
      // Update infinite queries with any filters
      queryClient.setQueriesData<
        InfiniteData<SuccessResponseDto<PaginatedNotificationResponse>>
      >(
        {
          predicate: query =>
            query.queryKey[0] === 'splitfiesta' &&
            query.queryKey[1] === 'notifications' &&
            query.queryKey[2] === 'infinite',
        },
        oldData => {
          if (!oldData) return oldData;

          // Check if notification exists and is unread in THIS query
          let wasUnreadInQuery = false;
          for (const page of oldData.pages) {
            const notification = page.data.items.find(
              n => n.id === notificationId
            );
            if (notification && !notification.isRead) {
              wasUnreadInQuery = true;
              break;
            }
          }

          return {
            ...oldData,
            pages: oldData.pages.map((page, pageIndex) => ({
              ...page,
              data: {
                ...page.data,
                items: page.data.items.map(notification =>
                  notification.id === notificationId
                    ? { ...notification, isRead: true }
                    : notification
                ),
                // Only update unread count on the first page
                unreadCount:
                  pageIndex === 0 && wasUnreadInQuery
                    ? Math.max(0, page.data.unreadCount - 1)
                    : page.data.unreadCount,
              },
            })),
          };
        }
      );

      // CRITICAL: Update the count query for bell counter (only once, outside the loop)
      if (notificationWasUnread) {
        queryClient.setQueryData(
          queryKeys.notificationsCount(),
          (oldCountData: unknown) => {
            if (!oldCountData || typeof oldCountData !== 'object')
              return oldCountData;
            const typedOldCountData = oldCountData as {
              data?: { unreadCount?: number };
            };
            return {
              ...typedOldCountData,
              data: {
                ...typedOldCountData.data,
                unreadCount: Math.max(
                  0,
                  (typedOldCountData.data?.unreadCount || 0) - 1
                ),
              },
            };
          }
        );
      }

      // Also update regular notifications queries
      queryClient.setQueriesData<
        SuccessResponseDto<PaginatedNotificationResponse>
      >(
        {
          predicate: query =>
            query.queryKey[0] === 'splitfiesta' &&
            query.queryKey[1] === 'notifications' &&
            query.queryKey[2] !== 'infinite' &&
            query.queryKey[2] !== 'settings',
        },
        oldData => {
          if (!oldData) return oldData;

          const notification = oldData.data.items.find(
            n => n.id === notificationId
          );
          const wasUnread = notification && !notification.isRead;

          return {
            ...oldData,
            data: {
              ...oldData.data,
              items: oldData.data.items.map(notification =>
                notification.id === notificationId
                  ? { ...notification, isRead: true }
                  : notification
              ),
              unreadCount: wasUnread
                ? Math.max(0, oldData.data.unreadCount - 1)
                : oldData.data.unreadCount,
            },
          };
        }
      );

      // Return context for potential rollback
      return { previousData };
    },
    onError: (_err, _notificationId, context) => {
      // If the mutation fails, use the context to roll back
      if (context?.previousData) {
        queryClient.setQueryData(
          queryKeys.notificationsInfinite(),
          context.previousData
        );
      }
    },
    onSuccess: () => {
      // Don't invalidate immediately - let the optimistic update persist
      // Schedule a background refetch after a delay to ensure consistency
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.notificationsInfinite(),
          refetchType: 'active', // Only refetch if query is actively being used
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.notifications(),
          refetchType: 'active',
        });
        // CRITICAL: Invalidate the count query to update the bell counter
        queryClient.invalidateQueries({
          queryKey: queryKeys.notificationsCount(),
          refetchType: 'active',
        });
      }, 1000); // Delay refetch by 1 second to let UI update smoothly
    },
  });
}

/**
 * Hard-delete one, many, or all notifications. Mirrors the mobile
 * hook's API: pass `notificationIds` for single/bulk delete, or
 * `deleteAll: true` to clear the caller's entire feed.
 *
 * Server batches via DynamoDB BatchWriteItem (25 ids per call), so
 * even clearing a 200-item feed costs ~8 DDB calls rather than 200.
 *
 * Optimistic: filters the deleted items out of every cached
 * `notificationsInfinite` variant and zeros / decrements the bell
 * count immediately. Falls back to a refetch on settle so the
 * authoritative server state catches up.
 */
export function useDeleteNotificationsMutation() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      notificationIds?: string[];
      deleteAll?: boolean;
    }) => {
      return ApiClientService.notifications.deleteNotifications(api, params);
    },
    onMutate: async params => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.notificationsInfinite(),
      });

      const snapshots = queryClient.getQueriesData<
        InfiniteData<SuccessResponseDto<PaginatedNotificationResponse>>
      >({
        predicate: q =>
          q.queryKey[0] === 'splitfiesta' &&
          q.queryKey[1] === 'notifications' &&
          q.queryKey[2] === 'infinite',
      });

      queryClient.setQueriesData<
        InfiniteData<SuccessResponseDto<PaginatedNotificationResponse>>
      >(
        {
          predicate: q =>
            q.queryKey[0] === 'splitfiesta' &&
            q.queryKey[1] === 'notifications' &&
            q.queryKey[2] === 'infinite',
        },
        oldData => {
          if (!oldData) return oldData;
          if (params.deleteAll) {
            return {
              ...oldData,
              pages: oldData.pages.map(page => ({
                ...page,
                data: { ...page.data, items: [], unreadCount: 0 },
              })),
            };
          }
          if (!params.notificationIds?.length) return oldData;
          const ids = new Set(params.notificationIds);
          return {
            ...oldData,
            pages: oldData.pages.map(page => {
              const removed = page.data.items.filter(i => ids.has(i.id));
              const removedUnread = removed.filter(i => !i.isRead).length;
              return {
                ...page,
                data: {
                  ...page.data,
                  items: page.data.items.filter(i => !ids.has(i.id)),
                  unreadCount: Math.max(
                    0,
                    page.data.unreadCount - removedUnread
                  ),
                },
              };
            }),
          };
        }
      );

      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      if (!ctx?.snapshots) return;
      for (const [key, data] of ctx.snapshots) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: () => {
      // Pull authoritative state once the dust settles — keeps the
      // bell counter and any other cached page in sync with the
      // server post-batch-delete.
      queryClient.invalidateQueries({
        queryKey: queryKeys.notificationsInfinite(),
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.notifications(),
        refetchType: 'active',
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.notificationsCount(),
        refetchType: 'active',
      });
    },
  });
}

/**
 * Hook to fetch notification settings
 */
export function useNotificationSettingsQuery(enabled = true) {
  const api = useApi();

  return useQuery({
    queryKey: queryKeys.notificationSettings(),
    queryFn: async () => {
      const response = await ApiClientService.notifications.getSettings(api);
      return response as SuccessResponseDto<NotificationSettingsResponseDto>;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - settings don't change often
    gcTime: 10 * 60 * 1000, // 10 minutes cache
  });
}

/**
 * Hook to update notification settings
 */
export function useUpdateNotificationSettingsMutation() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: UpdateNotificationSettingsDto) => {
      const response = await ApiClientService.notifications.updateSettings(
        api,
        settings
      );
      return response as SuccessResponseDto<NotificationSettingsResponseDto>;
    },
    onSuccess: data => {
      // Update cache with new settings
      queryClient.setQueryData(queryKeys.notificationSettings(), data);
    },
  });
}

/**
 * Hook to get unread notification count
 * Uses a dedicated query to ensure counter updates regardless of active filters
 */
export function useUnreadNotificationCount() {
  const api = useApi();

  const { data } = useQuery({
    queryKey: queryKeys.notificationsCount(),
    queryFn: async () => {
      const response = await ApiClientService.notifications.listWithCursor(
        api,
        {
          limit: 1, // Minimal fetch, we only need the count
          unreadOnly: false, // Get all to ensure accurate count
        }
      );
      return response;
    },
    staleTime: 0, // Always consider stale for immediate refresh
    gcTime: 2 * 60 * 1000, // 2 minutes cache
    refetchOnMount: 'always', // Always refetch on mount (PWA can't pull to refresh)
  });

  // Extract unread count from response
  const pageData = data?.data as { unreadCount?: number } | undefined;
  return pageData?.unreadCount || 0;
}

/**
 * Hook to get unseen notification count (for badge)
 * Uses Facebook-style two-tier notification system:
 * - Badge shows unseen count (clears when panel opens)
 * - Notifications remain unread (highlighted) until clicked
 */
export function useUnseenNotificationCount() {
  const api = useApi();

  const { data } = useQuery({
    queryKey: queryKeys.notificationsCount(),
    queryFn: async () => {
      const response = await ApiClientService.notifications.listWithCursor(
        api,
        {
          limit: 1, // Minimal fetch, we only need the count
          unreadOnly: false, // Get all to ensure accurate count
        }
      );
      return response;
    },
    staleTime: 0, // Always consider stale for immediate refresh
    gcTime: 2 * 60 * 1000, // 2 minutes cache
    refetchOnMount: 'always', // Always refetch on mount (PWA can't pull to refresh)
  });

  // Extract unseen count from response
  const pageData = data?.data as { unseenCount?: number } | undefined;
  return pageData?.unseenCount || 0;
}

/**
 * Hook to mark all notifications as seen (clears badge)
 * Called when user opens notification panel (Facebook-style UX)
 */
export function useMarkNotificationsAsSeenMutation() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await ApiClientService.notifications.markAsSeen(api);
      return response;
    },
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.notificationsCount(),
      });

      // Snapshot previous value
      const previousCountData = queryClient.getQueryData(
        queryKeys.notificationsCount()
      );

      // Optimistically update unseen count to 0
      queryClient.setQueryData(
        queryKeys.notificationsCount(),
        (oldData: unknown) => {
          if (!oldData || typeof oldData !== 'object') return oldData;
          const typedOldData = oldData as {
            data?: { unseenCount?: number; unreadCount?: number };
          };
          return {
            ...typedOldData,
            data: {
              ...typedOldData.data,
              unseenCount: 0, // Clear unseen count immediately
            },
          };
        }
      );

      // Also update all notifications to be marked as seen
      queryClient.setQueriesData<
        InfiniteData<SuccessResponseDto<PaginatedNotificationResponse>>
      >(
        {
          predicate: query =>
            query.queryKey[0] === 'splitfiesta' &&
            query.queryKey[1] === 'notifications' &&
            query.queryKey[2] === 'infinite',
        },
        oldData => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page, pageIndex) => ({
              ...page,
              data: {
                ...page.data,
                items: page.data.items.map(notification => ({
                  ...notification,
                  isSeen: true,
                })),
                // Reset unseen count to 0 on the first page
                unseenCount: pageIndex === 0 ? 0 : page.data.unseenCount,
              },
            })),
          };
        }
      );

      return { previousCountData };
    },
    onError: (_err, _params, context) => {
      // Roll back on error
      if (context?.previousCountData) {
        queryClient.setQueryData(
          queryKeys.notificationsCount(),
          context.previousCountData
        );
      }
    },
    onSuccess: () => {
      // Background refetch after delay
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: queryKeys.notificationsCount(),
          refetchType: 'active',
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.notificationsInfinite(),
          refetchType: 'active',
        });
      }, 1000);
    },
  });
}
